import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { loGAApi } from '../utils/api';
import { Download, Send, FileText, Calendar, CheckCircle, AlertCircle, Loader, Info, Upload, Database } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { motion } from 'framer-motion';

const LOGA_ALLOWED = ['vcoto@valorizasm.com', 'smorin@valorizasm.com', 'illorente@valorizasm.com'];

export default function LogaExportPage() {
  const { user } = useAuth();
  const isAllowed = user?.role === 'admin' || LOGA_ALLOWED.includes(user?.email);

  const today = new Date();
  const isAfter20 = today.getDate() > 20;
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString().split('T')[0];
  const day21 = new Date(today.getFullYear(), today.getMonth(), 21)
    .toISOString().split('T')[0];
  const eom = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    .toISOString().split('T')[0];
  const todayStr = today.toISOString().split('T')[0];

  // If we're past the 20th, default to the manual range (21st → end of month).
  // Otherwise, default to month-start → today (which is what the cron will send).
  const [startDate, setStartDate] = useState(isAfter20 ? day21 : firstOfMonth);
  const [endDate, setEndDate] = useState(isAfter20 ? eom : todayStr);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [sending, setSending] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [error, setError] = useState('');

  // Captio master
  const [captioStatus, setCaptioStatus] = useState(null);
  const [uploadingCaptio, setUploadingCaptio] = useState(false);
  const [captioMsg, setCaptioMsg] = useState('');
  const captioFileRef = useRef(null);

  const refreshCaptioStatus = async () => {
    try {
      const res = await loGAApi.captioStatus();
      setCaptioStatus(res.data);
    } catch (e) {
      setCaptioStatus(null);
    }
  };

  useEffect(() => {
    if (isAllowed) refreshCaptioStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed]);

  const handleCaptioUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCaptio(true);
    setCaptioMsg('');
    setError('');
    try {
      const res = await loGAApi.captioImport(file);
      setCaptioMsg(`${res.data.message} — ${res.data.imported} empleados (omitidos sin email/PNR: ${res.data.skipped})`);
      await refreshCaptioStatus();
      // Also refresh the LOGA preview because resolutions may have changed
      if (startDate && endDate && endDate >= startDate) {
        const r = await loGAApi.preview(startDate, endDate);
        setPreview(r.data);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al importar Captio');
    } finally {
      setUploadingCaptio(false);
      if (captioFileRef.current) captioFileRef.current.value = '';
    }
  };

  useEffect(() => {
    if (startDate && endDate && endDate >= startDate) {
      fetchPreview();
    }
  }, [startDate, endDate]);

  const fetchPreview = async () => {
    setLoadingPreview(true);
    setError('');
    try {
      const res = await loGAApi.preview(startDate, endDate);
      setPreview(res.data);
    } catch (e) {
      setPreview(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    setError('');
    try {
      const res = await loGAApi.export(startDate, endDate);
      const filename = preview?.filename || `impvar_AUSENCIAS_${startDate}.${endDate}.csv`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al generar el fichero');
    } finally {
      setDownloading(false);
    }
  };

  const handleSendEmail = async () => {
    setSending(true);
    setError('');
    setSuccessMsg('');
    try {
      const res = await loGAApi.sendEmail(startDate, endDate);
      setSuccessMsg(`${res.data.message} — ${res.data.records} registros`);
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al enviar el fichero');
    } finally {
      setSending(false);
    }
  };

  if (!isAllowed) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
          <p className="text-muted-foreground">No tienes permisos para acceder a esta sección.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-6">
      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 shadow-md">
            <FileText className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-heading">Exportación LOGA</h1>
            <p className="text-sm text-muted-foreground">Genera el fichero de ausencias para el sistema de nóminas</p>
          </div>
        </div>
      </motion.div>

      {/* Alert after day 20 */}
      {isAfter20 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
          className="flex items-start gap-3 p-4 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800"
        >
          <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-orange-700 dark:text-orange-300">
            <p className="font-semibold">Atención: ya pasó el día 20</p>
            <p>El fichero mensual ya fue enviado automáticamente. Para las aprobaciones del 21 al fin de mes usa este formulario para gestionarlas manualmente.</p>
          </div>
        </motion.div>
      )}

      {/* Captio master panel */}
      <motion.div
        initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-card border border-border rounded-2xl p-5 shadow-sm"
        data-testid="captio-master-panel"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3 min-w-0">
            <div className="p-2 rounded-lg bg-violet-100 dark:bg-violet-900/30 flex-shrink-0">
              <Database className="h-4 w-4 text-violet-600 dark:text-violet-400" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm font-semibold">Maestro Captio (MAN / AK / PNR / VERTNR)</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Sube el fichero <code className="text-[10px] bg-muted/60 px-1 rounded">SCOUTD_*_Integracion_Captio_VSM.csv</code> para que el CSV LOGA use los códigos correctos por empleado.
              </p>
              {captioStatus?.total_entries > 0 ? (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-emerald-700 dark:text-emerald-400 font-medium" data-testid="captio-status-loaded">
                    {captioStatus.total_entries.toLocaleString()} empleados cargados
                  </span>
                  <span className="text-muted-foreground">
                    · Última actualización: {captioStatus.last_updated ? new Date(captioStatus.last_updated).toLocaleString('es-ES') : '—'}
                  </span>
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <AlertCircle className="h-3.5 w-3.5 text-orange-500" />
                  <span className="text-orange-700 dark:text-orange-400 font-medium" data-testid="captio-status-empty">
                    Sin maestro Captio cargado — el CSV usará valores por defecto (MAN=M0001, AK=898)
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <input
              type="file"
              accept=".csv"
              ref={captioFileRef}
              onChange={handleCaptioUpload}
              className="hidden"
              data-testid="captio-file-input"
            />
            <Button
              onClick={() => captioFileRef.current?.click()}
              disabled={uploadingCaptio}
              data-testid="captio-upload-btn"
              variant="outline"
              size="sm"
              className="gap-2 border-violet-200 dark:border-violet-800 text-violet-700 dark:text-violet-300 hover:bg-violet-50 dark:hover:bg-violet-900/20"
            >
              {uploadingCaptio
                ? <Loader className="h-3.5 w-3.5 animate-spin" />
                : <Upload className="h-3.5 w-3.5" />
              }
              {captioStatus?.total_entries > 0 ? 'Actualizar Captio' : 'Subir Captio CSV'}
            </Button>
          </div>
        </div>
        {captioMsg && (
          <div className="mt-3 p-2.5 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-2" data-testid="captio-success">
            <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
            {captioMsg}
          </div>
        )}
      </motion.div>


      {/* Date range */}
      <motion.div
        initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-card border border-border rounded-2xl p-6 shadow-sm"
      >
        <h2 className="text-base font-semibold mb-4 flex items-center gap-2">
          <Calendar className="h-4 w-4 text-brand" /> Selecciona el rango de fechas
        </h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="space-y-1.5">
            <Label htmlFor="loga-start">Fecha inicio</Label>
            <Input
              id="loga-start" type="date" value={startDate}
              onChange={e => setStartDate(e.target.value)}
              data-testid="loga-start-date"
              className="h-10"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="loga-end">Fecha fin</Label>
            <Input
              id="loga-end" type="date" value={endDate}
              min={startDate}
              onChange={e => setEndDate(e.target.value)}
              data-testid="loga-end-date"
              className="h-10"
            />
          </div>
        </div>

        {/* Preview */}
        <div className="p-4 rounded-xl bg-muted/40 border border-border/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              {loadingPreview
                ? <Loader className="h-4 w-4 animate-spin text-blue-500" />
                : <FileText className="h-4 w-4 text-blue-600" />
              }
            </div>
            <div>
              {preview ? (
                <>
                  <div className="text-sm font-semibold">{preview.filename}</div>
                  <div className="text-xs text-muted-foreground">
                    {preview.count} vacaciones aprobadas · {startDate} al {endDate}
                    {preview.missing_pnr_count > 0 && (
                      <span className="ml-1 text-orange-600 dark:text-orange-400 font-medium">
                        · {preview.missing_pnr_count} sin PNR (excluidas)
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">
                  {loadingPreview ? 'Cargando...' : 'Sin resultados en el rango seleccionado'}
                </div>
              )}
            </div>
          </div>
          {preview && (
            <div className="text-2xl font-bold text-brand font-heading">{preview.count}</div>
          )}
        </div>

        {/* Missing PNR warning */}
        {preview?.missing_pnr_count > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
            className="mt-4 p-4 rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800"
            data-testid="loga-missing-pnr-warning"
          >
            <div className="flex items-start gap-2 mb-2">
              <AlertCircle className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-orange-700 dark:text-orange-300">
                <p className="font-semibold">{preview.missing_pnr_count} solicitud(es) sin PNR resoluble — quedarán fuera del CSV</p>
                <p className="text-xs mt-1">
                  LOGA no acepta registros sin PNR. El sistema ya intentó resolverlo por DNI y email
                  sin éxito. Al descargar o enviar, se notificará automáticamente a vcoto + smorin + illorente
                  con los siguientes casos para revisión manual:
                </p>
              </div>
            </div>
            <div className="ml-6 mt-3 max-h-48 overflow-y-auto bg-white dark:bg-slate-900/40 rounded-lg border border-orange-200 dark:border-orange-800">
              <table className="w-full text-xs">
                <thead className="bg-orange-100 dark:bg-orange-900/40 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">Empleado</th>
                    <th className="text-left px-3 py-2 font-medium">Email</th>
                    <th className="text-left px-3 py-2 font-medium">DNI</th>
                    <th className="text-left px-3 py-2 font-medium">Periodo</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.missing_pnr_list?.map((m, idx) => (
                    <tr key={m.vacation_id || idx} className="border-t border-orange-100 dark:border-orange-900/40">
                      <td className="px-3 py-1.5">
                        {[m.first_name, m.last_name].filter(Boolean).join(' ') || <span className="italic text-slate-400">(usuario no encontrado)</span>}
                      </td>
                      <td className="px-3 py-1.5 text-slate-600 dark:text-slate-400">{m.email || '—'}</td>
                      <td className="px-3 py-1.5 text-slate-600 dark:text-slate-400">{m.dni || '—'}</td>
                      <td className="px-3 py-1.5 text-slate-600 dark:text-slate-400">{m.start_date} → {m.end_date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Actions */}
      <motion.div
        initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-card border border-border rounded-2xl p-6 shadow-sm space-y-4"
      >
        <h2 className="text-base font-semibold">Acciones</h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {/* Download */}
          <Button
            onClick={handleDownload}
            disabled={downloading || !preview?.count}
            data-testid="loga-download-btn"
            className="h-12 flex items-center gap-2 bg-gradient-to-r from-brand to-green-500 hover:from-brand-hover hover:to-green-600 text-white shadow-md"
          >
            {downloading
              ? <Loader className="h-4 w-4 animate-spin" />
              : <Download className="h-4 w-4" />
            }
            <div className="text-left">
              <div className="text-sm font-semibold">Descargar CSV</div>
              <div className="text-xs opacity-80">Descarga local del fichero</div>
            </div>
          </Button>

          {/* Send by email */}
          <Button
            onClick={handleSendEmail}
            disabled={sending || !preview?.count}
            variant="outline"
            data-testid="loga-send-btn"
            className="h-12 flex items-center gap-2 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20"
          >
            {sending
              ? <Loader className="h-4 w-4 animate-spin" />
              : <Send className="h-4 w-4" />
            }
            <div className="text-left">
              <div className="text-sm font-semibold">Enviar por email</div>
              <div className="text-xs opacity-70">vcoto + smorin + illorente</div>
            </div>
          </Button>
        </div>

        {successMsg && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="flex items-center gap-2 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-300"
          >
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            {successMsg}
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="flex items-center gap-2 p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-sm text-destructive"
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </motion.div>
        )}
      </motion.div>

      {/* Info panel */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4"
      >
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-blue-700 dark:text-blue-300 space-y-1">
            <p className="font-semibold">Información sobre el proceso mensual</p>
            <p>El día 20 de cada mes el sistema envía automáticamente el fichero a vcoto, smorin e illorente con todas las vacaciones aprobadas del mes.</p>
            <p>Para las solicitudes aprobadas <strong>entre el día 21 y el fin de mes</strong>, usa este formulario para descargar o enviar manualmente el fichero correspondiente. Cualquier aprobación realizada <strong>después del día 20</strong> también genera un email de alerta automático.</p>
            <p>Nombre del fichero: <code className="bg-blue-100 dark:bg-blue-800 px-1 rounded">impvar_AUSENCIAS_DDMMYYinicio.DDMMYYfin.csv</code></p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
